import { useTranslation } from "react-i18next";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import { Talent } from "../../talents/talentAPI";
import { EditMode } from "../TalentDetails";
import { getTalentContentKey, TalentContentKey } from "../constants";
import TalentEditContactDetails from "./TalentEditContactDetails";
import TalentEditSkills from "./TalentEditSkills";
import TalentEditWorkTitles from "./TalentEditWorkTitles";
import TalentAddEducation from "./educations/TalentAddEducation";
import TalentEditEducation from "./educations/TalentEditEducation";
import TalentAddLanguage from "./languages/TalentAddLanguage";
import TalentEditLanguage from "./languages/TalentEditLanguage";
import TalentAddRecommender from "./recommenders/TalentAddRecommender";
import TalentEditRecommender from "./recommenders/TalentEditRecommender";
import TalentEditShiftSalariesForm from "./salaries/TalentEditShiftSalariesForm";
import TalentShiftSalariesForm from "./salaries/TalentShiftSalariesForm";
import TalentAddTraining from "./trainings/TalentAddTraining";
import TalentEditTraining from "./trainings/TalentEditTraining";
import TalentAddWorkHistoryEntry from "./workHistoryEntry/TalentAddWorkHistoryEntry";
import TalentEditWorkHistoryEntry from "./workHistoryEntry/TalentEditWorkHistoryEntry";
import WorkPermitFormContainer from "./workPermits/WorkPermitFormContainer";
import { TalentEditCV } from "./TalentEditCV";

type Props = {
  editMode: EditMode;
  handleCloseEditModal: () => void;
  handleCloseOnExited: () => void;
  talent: Talent;
};

const TalentDetailsEditFormContainer = ({
  editMode,
  handleCloseEditModal,
  handleCloseOnExited,
  talent,
}: Props) => {
  const { t } = useTranslation();

  const editForms = {
    contactDetails: {
      component: (
        <TalentEditContactDetails
          onClose={handleCloseEditModal}
          talent={talent}
        />
      ),
      modalProps: {},
    },
    workTitles: {
      component: <TalentEditWorkTitles onClose={handleCloseEditModal} />,
      modalProps: {},
    },
    skills: {
      component: <TalentEditSkills onClose={handleCloseEditModal} />,
      modalProps: {},
    },
    educations_edit: {
      component: (
        <TalentEditEducation
          editMode={editMode}
          onClose={handleCloseEditModal}
        />
      ),
      modalProps: {},
    },
    educations_add: {
      component: <TalentAddEducation onClose={handleCloseEditModal} />,
      modalProps: {},
    },
    recommenders_edit: {
      component: (
        <TalentEditRecommender
          editMode={editMode}
          onClose={handleCloseEditModal}
        />
      ),
      modalProps: {},
    },
    recommenders_add: {
      component: <TalentAddRecommender onClose={handleCloseEditModal} />,
      modalProps: {},
    },
    workHistory_add: {
      component: <TalentAddWorkHistoryEntry onClose={handleCloseEditModal} />,
      modalProps: {
        sx: {
          width: "500px",
        },
      },
    },
    workHistory_edit: {
      component: (
        <TalentEditWorkHistoryEntry
          onClose={handleCloseEditModal}
          editMode={editMode}
        />
      ),
      modalProps: {
        sx: {
          width: "500px",
        },
      },
    },
    languages_add: {
      component: <TalentAddLanguage onClose={handleCloseEditModal} />,
      modalProps: {},
    },
    languages_edit: {
      component: (
        <TalentEditLanguage
          editMode={editMode}
          onClose={handleCloseEditModal}
        />
      ),
      modalProps: {},
    },
    shiftSalaries_add: {
      component: (
        <TalentShiftSalariesForm
          talent={talent}
          onClose={handleCloseEditModal}
          editMode={editMode}
        />
      ),
      modalProps: {},
    },
    shiftSalaries_edit: {
      component: (
        <TalentEditShiftSalariesForm
          editMode={editMode}
          talent={talent}
          onClose={handleCloseEditModal}
        />
      ),
      modalProps: {
        sx: {
          maxWidth: "1000px",
        },
        autoWidth: true,
      },
    },
    [getTalentContentKey(TalentContentKey.WorkPermits, "add")]: {
      component: (
        <WorkPermitFormContainer
          title={t("talentProfile:workPermitsTitle")}
          onClose={handleCloseEditModal}
          fixedType={editMode?.fixedType}
        />
      ),
      modalProps: {},
    },
    [getTalentContentKey(TalentContentKey.WorkPermits, "edit")]: {
      component: (
        <WorkPermitFormContainer
          title={t("talentProfile:workPermitsTitle")}
          editMode={editMode}
          onClose={handleCloseEditModal}
        />
      ),
      modalProps: {},
    },
    [getTalentContentKey(TalentContentKey.DrivingLicenses, "add")]: {
      component: (
        <WorkPermitFormContainer
          title={t("talentProfile:drivingLicensesTitle")}
          drivingLicenseMode
          onClose={handleCloseEditModal}
        />
      ),
      modalProps: {},
    },
    [getTalentContentKey(TalentContentKey.DrivingLicenses, "edit")]: {
      component: (
        <WorkPermitFormContainer
          title={t("talentProfile:drivingLicensesTitle")}
          editMode={editMode}
          drivingLicenseMode
          onClose={handleCloseEditModal}
        />
      ),
      modalProps: {},
    },
    trainings_add: {
      component: <TalentAddTraining onClose={handleCloseEditModal} />,
      modalProps: {},
    },
    trainings_edit: {
      component: (
        <TalentEditTraining
          onClose={handleCloseEditModal}
          editMode={editMode}
        />
      ),
      modalProps: {},
    },
    cv_edit: {
      component: (
        <TalentEditCV onClose={handleCloseEditModal} talentId={talent.id} />
      ),
      modalProps: {},
    },
    cv_add: {
      component: (
        <TalentEditCV onClose={handleCloseEditModal} talentId={talent.id} />
      ),
      modalProps: {},
    },
    // rest of the edit forms
  };

  return (
    <DialogComponent
      variant="form"
      open={editMode.enabled}
      onClose={handleCloseEditModal}
      disableBackdropClick={false}
      {...(editMode.content && { ...editForms[editMode.content]?.modalProps })}
      TransitionProps={{ onExited: handleCloseOnExited }}
    >
      {editMode.content && editForms[editMode.content].component}
    </DialogComponent>
  );
};

export default TalentDetailsEditFormContainer;
